import { QUICKBOOKS_ENDPOINT } from '../endpoints';
import API from './../api';
import { WebSocketClient } from '../websocket/client';

export default {
    namespaced: true,

    state() {
        return {
            quickbooksData: {
                one_class_per_line_detail: false,
            },
            accountMapping: [],
            accountChoices: [],
            classChoices: [],
            finishedInitialLoading: false,
            loadingAccountChoices: false,
            loadingClassChoices: false,
            isSynchronizing: false,
            syncProgress: 0,
            syncErrors: 0,
            syncStage: null,
            websocketClient: null,
            toastMessage: null,
            toastType: null,
            autoRefreshReservations: true,
            shouldRefreshReservations: false,
            showErrorsOnly: false,

        };
    },

    getters: {
        quickbooksData: (state) => state.quickbooksData,
        accountMapping: (state) => state.accountMapping,
        accountChoices: (state) => state.accountChoices,
        classChoices: (state) => state.classChoices,
        finishedInitialLoading: (state) => state.finishedInitialLoading,
        loadingAccountChoices:  (state) => state.loadingAccountChoices,
        loadingClassChoices:  (state) => state.loadingClassChoices,
        isSynchronizing: (state) => state.isSynchronizing,
        syncProgress: (state) => state.syncProgress,
        syncErrors: (state) => state.syncErrors,
        syncStage: (state) => state.syncStage,
        toastMessage: (state) => state.toastMessage,
        toastType: (state) => state.toastType,
        autoRefreshReservations: (state) => state.autoRefreshReservations,
        shouldRefreshReservations: (state) => state.shouldRefreshReservations,
        showErrorsOnly: (state) => state.showErrorsOnly,
    },

    mutations: {
        gotQuickbooksdata(state, response) {
            state.quickbooksData = response.data;
        },
        gotAccountMapping(state, response) {
            state.accountMapping = response.data;
        },
        gotAccountChoices(state, response) {
            state.accountChoices = response.data;
        },
        gotClassChoices(state, response) {
            state.classChoices = response.data;
        },        
        gotFirstRun(state, data) {
            state.quickbooksData.first_run = data;
        },
        setFinishedInitialLoading(state, value) {
            state.finishedInitialLoading = value;
        },
        setSynchronizing(state, value) {
            state.isSynchronizing = value;
        },
        setSyncProgress(state, progress) {
            state.syncProgress = progress;
        },
        setSyncErrors(state, errors) {
            state.syncErrors = errors;
        },        
        setSyncStage(state, stage) {
            state.syncStage = stage;
        },
        setAutoRefreshReservations(state, value) {
            state.autoRefreshReservations = value;
        },
        setShowErrorsOnly(state, value) {
            state.showErrorsOnly = value;
        },              
        setWebsocketClient(state, client) {
            state.websocketClient = client;
        },
        setOneClassPerLineDetail(state, value) {
            state.quickbooksData.one_class_per_line_detail = value;
        },        
        closeWebsocket(state) {
            if (state.websocketClient) {
                state.websocketClient.close();
                state.websocketClient = null;
            }
        },
        setToast(state, { message, type }) {
            state.toastMessage = message;
            state.toastType = type;
        },
        clearToast(state) {
            state.toastMessage = null;
            state.toastType = null;
        },
        triggerReservationsRefresh(state) {
            state.shouldRefreshReservations = !state.shouldRefreshReservations
        },        
    },

    actions: {
        async fetchData({ commit }) {
            try {
                commit('gotQuickbooksdata', await API.get(QUICKBOOKS_ENDPOINT()));
            } finally {
                commit('setFinishedInitialLoading', true);
            }            
        },
        async fetchAccountMapping({ commit }) {
            commit('gotAccountMapping', await API.get(`${QUICKBOOKS_ENDPOINT()}account-mapping/`));
        },
        async fetchAccountChoices({ state, commit }) {
            if (state.loadingAccountChoices) return; 
        
            state.loadingAccountChoices = true;
        
            try {
                commit('gotAccountChoices', await API.get(`${QUICKBOOKS_ENDPOINT()}account-choices/`));
            } finally {
                state.loadingAccountChoices = false; 
            }
        },
        async fetchClassChoices({ state, commit }) {
            if (state.loadingClassChoices) return; 
        
            state.loadingClassChoices = true;
        
            try {
                commit('gotClassChoices', await API.get(`${QUICKBOOKS_ENDPOINT()}class-choices/`));
            } finally {
                state.loadingClassChoices = false; 
            }            
        },
        setSynchronizing({ commit }, value) {
            commit('setSynchronizing', value);
        },
        handleObjectsRefresh({ commit, state }, message) {
            const refreshObjects = getRefreshObjects(message.msg)
            if (refreshObjects) {
                if (refreshObjects && state.autoRefreshReservations) {
                    commit('triggerReservationsRefresh')
                }
            }            
        },
        handleWebsocketMessage({ commit, state, dispatch }, message) {
            const progress = getProgress(message.msg, state.syncProgress);
            const stage = getStage(message.msg, state.syncStage);
            const errors = getErrors(message.msg, state.syncErrors);
            commit('setSyncProgress', progress);
            commit('setSyncStage', stage);
            commit('setSyncErrors', Number(errors));
        
            dispatch('handleObjectsRefresh', message)
            

            if (Number(errors) === -1) {
                commit('setToast', { message: 'QuickBooks sync failed with undefined error(s).', type: 'error' });
                commit('setSynchronizing', false);
            } else if (Number(errors) > 0) {
                commit('setToast', { message: `QuickBooks sync failed with ${errors} error(s). Please check Sync Fail Log`, type: 'error' });
                commit('setSynchronizing', false);
            }
            commit('setSyncErrors', 0);
            if (progress === 100) {
                commit('setSynchronizing', false);
                commit('setToast', { message: 'Synchronization completed', type: 'success' });
                commit('closeWebsocket');
            }
        },
        connectWebsocket({ commit, state, dispatch }) {
            if (!state.websocketClient) {
                const websocketClient = new WebSocketClient();
                websocketClient.initialize({ token: state.quickbooksData.websocket_token });
                websocketClient.setMessageCallback((message) => dispatch('handleWebsocketMessage', message));
                commit('setWebsocketClient', websocketClient);
            }
        },
        async synchronize({ commit, state, dispatch }, { firstrun_date }) {
            try {
                commit('setSyncProgress', 1);
                commit('setSynchronizing', true);
                dispatch('connectWebsocket');
                await API.post(QUICKBOOKS_ENDPOINT() + 'synchronize/', {
                    firstrun_date,
                    token: state.quickbooksData.websocket_token,
                });
                commit('gotFirstRun', false);
            } catch (e) {
                console.log(e);
                commit('setSyncProgress', 0);
                commit('setSynchronizing', false);
                commit('closeWebsocket');
                commit('setToast', { message: 'Could not synchronize QuickBooks data', type: 'error' });
                throw e;
            }
        },
        resumeWebsocket({ dispatch }) {
            dispatch('connectWebsocket');
        },
        setOneClassPerLineDetail({ commit }, value) {
            commit('setOneClassPerLineDetail', value);
        },        
    },
};

function getRefreshObjects(str, current) {
    const regexp = /(REFRESH_OBJECTS),(\w+)/i;
    const result = str && str.match(regexp);
    return (result && result[2]) || false;
}

function getProgress(str, current) {
    const regexp = /(PROGRESS),(\d+)/i;
    const result = str && str.match(regexp);
    return (result && Number(result[2])) || current;
}

function getErrors(str, current) {
    const regexp = /(ENABLE_BUTTON),(\d+)/i;
    const result = str && str.match(regexp);
    return (result && Number(result[2])) || current;
}

function getStage(str, current) {
    const regexp = /(SETSTAGE),(\w+)/i;
    const result = str && str.match(regexp);
    return (result && result[2]) || current;
}


